<template>
  <div class="list-repair-slips main row">
    <div class="col-md-12">
      <KTCodePreview v-bind:title="'Phiếu sửa chữa'">
        <template v-slot:toolbar>
          <b-button
            v-if="canExport"
            variant="success"
            size="sm"
            class="mr-2"
            :disabled="exporting"
            v-b-tooltip.hover.bottom
            :title="exportTooltip"
            @click="onExportExcel"
          >
            <b-spinner v-if="exporting" small class="mr-1"></b-spinner>
            <i v-else class="far fa-file-excel"></i>
            {{ exporting ? 'Đang xuất...' : 'Xuất Excel' }}
          </b-button>
          <b-button
            v-if="canCreate"
            variant="primary"
            size="sm"
            @click="goToCreate"
          >
            <i class="fas fa-plus"></i> Tạo phiếu mới
          </b-button>
        </template>

        <template v-slot:preview>
          <!-- Filter bar — 6 cột md=2 đều nhau / hàng (tham khảo trang Danh sách đơn hàng). -->
          <b-row class="mb-3 filter-row">
            <b-col md="2">
              <date-picker
                placeholder="Tạo từ"
                class="form-control form-control-sm"
                :config="dpConfigs.date"
                v-model="filter.fromDate"
              ></date-picker>
            </b-col>
            <b-col md="2">
              <date-picker
                placeholder="Tạo đến"
                class="form-control form-control-sm"
                :config="dpConfigs.date"
                v-model="filter.toDate"
              ></date-picker>
            </b-col>
            <b-col md="2">
              <date-picker
                placeholder="Trả từ"
                class="form-control form-control-sm"
                :config="dpConfigs.date"
                v-model="filter.returnedFromDate"
              ></date-picker>
            </b-col>
            <b-col md="2">
              <date-picker
                placeholder="Trả đến"
                class="form-control form-control-sm"
                :config="dpConfigs.date"
                v-model="filter.returnedToDate"
              ></date-picker>
            </b-col>
            <b-col md="2">
              <BDropdownCustom
                v-model="selectedStores"
                :options="storeOptions"
                placeholder="cửa hàng"
                :searchable="true"
                :matchKey="['name', 'shortName']"
                :limit="5"
                :disabled="storeLoading"
              />
            </b-col>
            <b-col md="2">
              <b-form-select
                size="sm"
                v-model="filter.status"
                :options="statusFilterOptions"
              ></b-form-select>
            </b-col>
          </b-row>
          <b-row class="mb-4 filter-row">
            <b-col md="2">
              <Autosuggest
                :model="technicianSearchText"
                :suggestions="employeeOptions"
                suggestionName="suggestionName"
                placeholder="Nhân viên KTV"
                :limit="20"
                @update:model="(v) => (technicianSearchText = v)"
                @change="onInputTechnician"
                @select="onSelectTechnician"
              />
            </b-col>
            <b-col md="2">
              <b-input
                placeholder="ID HĐ bán lẻ"
                size="sm"
                v-model.trim="filter.serviceBillNumber"
                autocomplete="off"
                v-on:keyup.enter="onFilter"
              ></b-input>
            </b-col>
            <b-col md="2">
              <b-input
                placeholder="ID HĐ bảo hành"
                size="sm"
                v-model.trim="filter.warrantyBillNumber"
                autocomplete="off"
                v-on:keyup.enter="onFilter"
              ></b-input>
            </b-col>
            <b-col md="2">
              <BDropdownCustom
                v-model="selectedCurrentStores"
                :options="storeOptions"
                placeholder="vị trí máy"
                :searchable="true"
                :matchKey="['name', 'shortName']"
                :limit="5"
                :disabled="storeLoading"
              />
            </b-col>
            <b-col md="2">
              <b-input
                placeholder="Số phiếu, KH, SĐT, IMEI, SP"
                size="sm"
                v-model.trim="filter.search"
                autocomplete="off"
                v-on:keyup.enter="onFilter"
              ></b-input>
            </b-col>
            <b-col md="2">
              <b-button-group size="sm" class="w-100">
                <b-button
                  class="font-weight-bolder btn-filter-apply"
                  variant="primary"
                  :disabled="!!filterRangeError"
                  v-b-tooltip.hover
                  :title="filterRangeError || 'Áp dụng bộ lọc'"
                  @click="onFilter"
                >
                  <i class="fas fa-search text-white fa-lg"></i>Lọc
                </b-button>
                <b-button
                  variant="warning"
                  @click="onResetFilter"
                  v-b-tooltip.hover
                  title="Xoá bộ lọc"
                >
                  <i class="fas fa-redo text-white"></i>
                </b-button>
              </b-button-group>
            </b-col>
          </b-row>

          <!-- Inline alert: khoảng ngày invalid → hiển thị persistent (toast dễ miss). -->
          <b-alert
            :show="!!filterRangeError"
            variant="warning"
            class="py-2 mb-3"
          >
            <i class="fas fa-exclamation-triangle mr-1"></i>
            {{ filterRangeError }}
          </b-alert>

          <!-- Table -->
          <b-table
            :items="rows"
            :fields="fields"
            :busy="loading"
            bordered
            hover
            show-empty
            empty-text="Không có phiếu sửa chữa nào"
            class="table-vertical-center"
          >
            <template #table-busy>
              <div class="text-center text-primary my-2">
                <b-spinner class="align-middle"></b-spinner>
                <strong>Đang tải...</strong>
              </div>
            </template>

            <template #cell(stt)="data">
              {{ (page - 1) * limit + data.index + 1 }}
            </template>

            <template #cell(slipNumber)="{ item }">
              <div>
                <strong>{{ item.slipNumber }}</strong>
              </div>
              <div class="text-muted small">
                {{ formatDateTime(item.createdAt) }}
              </div>
            </template>

            <template #cell(customer)="{ item }">
              <div>{{ item.customerName }}</div>
              <div class="text-muted small">{{ item.customerPhone }}</div>
            </template>

            <template #cell(product)="{ item }">
              <div>{{ item.productName || '—' }}</div>
              <div class="text-muted small">
                IMEI: {{ item.imei || '—' }}
                <b-badge v-if="item.isExternalDevice" variant="warning" class="ml-1">
                  Hàng ngoài
                </b-badge>
              </div>
            </template>

            <template #cell(warrantyProductName)="{ item }">
              <span v-if="item.warrantyProductName">{{ item.warrantyProductName }}</span>
              <span v-else class="text-muted">—</span>
            </template>

            <template #cell(currentStore)="{ item }">
              <span v-if="isAtOriginStore(item)" class="text-muted">
                {{ (item.currentStore && item.currentStore.name) || '—' }}
              </span>
              <span v-else class="text-warning font-weight-bold">
                <i class="fas fa-map-marker-alt mr-1"></i>
                {{ (item.currentStore && item.currentStore.name) || ('CH #' + item.currentStoreId) }}
              </span>
            </template>

            <template #cell(status)="{ item }">
              <b-badge :variant="statusVariant(item.status)">
                {{ statusLabel(item.status) }}
              </b-badge>
              <div v-if="isTransferring(item)" class="mt-2">
                <span class="transfer-chip">
                  <span class="transfer-chip__dot"></span>
                  <i class="fas fa-truck-moving transfer-chip__icon"></i>
                  Đang luân chuyển
                </span>
              </div>
            </template>

            <template v-slot:cell(actions)="row">
              <div class="d-flex justify-content-center">
                <b-dropdown size="sm" no-caret right variant="link" toggle-class="p-0">
                  <template #button-content>
                    <i class="flaticon2-settings"></i>
                  </template>
                  <b-dropdown-item @click="goToEdit(row.item.id)">
                    <span class="action-item-default">
                      <i class="flaticon2-sheet icon-size-sm"></i>
                      &nbsp; Xem / Sửa
                    </span>
                  </b-dropdown-item>
                  <!-- SRS t101348: lập HĐ bán lẻ linh kiện tại CH gốc sau khi máy luân chuyển
                       trả về (phiếu đã 'completed' nên không còn auto-redirect). -->
                  <b-dropdown-item
                    v-if="canCreatePartsBill(row.item)"
                    @click="goToCreatePartsBill(row.item.id)"
                  >
                    <span class="action-item-bill">
                      <i class="fas fa-file-invoice-dollar icon-size-sm"></i>
                      &nbsp; Lập HĐ bán lẻ linh kiện
                    </span>
                  </b-dropdown-item>
                  <b-dropdown-item
                    v-if="row.item.serviceBill && row.item.serviceBill.billNumber"
                    @click="openLinkedBill(row.item, 'service')"
                  >
                    <span class="action-item-link">
                      <i class="far fa-file-alt icon-size-sm"></i>
                      &nbsp; HĐ Bán lẻ #{{ row.item.serviceBill.billNumber }}
                    </span>
                  </b-dropdown-item>
                  <b-dropdown-item
                    v-if="row.item.warrantyBill && row.item.warrantyBill.billNumber"
                    @click="openLinkedBill(row.item, 'warranty')"
                  >
                    <span class="action-item-warranty">
                      <i class="fas fa-shield-alt icon-size-sm"></i>
                      &nbsp; HĐ Bảo hành #{{ row.item.warrantyBill.billNumber }}
                    </span>
                  </b-dropdown-item>
                  <b-dropdown-item
                    v-if="canDelete && row.item.status === SLIP_STATUS_RECEIVING"
                    @click="onDelete(row.item)"
                  >
                    <span class="action-item-danger">
                      <i class="flaticon2-rubbish-bin icon-size-sm"></i>
                      &nbsp; Xoá
                    </span>
                  </b-dropdown-item>
                </b-dropdown>
              </div>
            </template>
          </b-table>

          <b-row class="mt-3 align-items-center">
            <b-col md="4">
              <p class="text-dark mb-0 list-total-label">
                Tổng số phiếu: {{ totalRow }}
              </p>
            </b-col>
            <b-col md="2">
              <b-form-select
                v-model="limit"
                :options="pageSizeOptions"
                size="sm"
                @change="onChangePageSize"
              ></b-form-select>
            </b-col>
            <b-col md="6">
              <b-pagination
                v-show="totalPage >= 2"
                v-model="page"
                :total-rows="totalRow"
                :per-page="limit"
                align="right"
                size="sm"
                @change="onPageChange"
              ></b-pagination>
            </b-col>
          </b-row>
        </template>
      </KTCodePreview>
    </div>
  </div>
</template>

<script>
import KTCodePreview from '@/view/content/CodePreview.vue';
import ApiService from '@/core/services/api.service';
import moment from 'moment';
import BDropdownCustom from '@/view/base/bootstrap/BDropdownCustom.vue';
import Autosuggest from '@/view/base/auto-sugguest/AutoSuggest.vue';
import { xoa_dau } from '@/utils/common';
import { checkPermission } from '@/utils/saveDataToLocal';
import { makeToastSuccess, makeToastFaile } from '@/utils/common';
import { DP_CONFIG } from '@/utils/date';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import fileDownload from '@/utils/file-download';
import {
  SLIP_STATUS,
  SLIP_STATUS_OPTIONS as STATUS_OPTIONS,
  TRANSFER_STATUS,
} from '@/utils/warranty-service.constants';
import { extractError, formatDateTime, toIsoDate } from '@/utils/warranty-service.helpers';

const STATUS_FILTER_OPTIONS = Object.freeze([
  { value: '', text: 'Tất cả trạng thái' },
  ...STATUS_OPTIONS.map((s) => ({ value: s.value, text: s.text })),
]);

const PAGE_SIZE_OPTIONS = Object.freeze([
  { value: 10, text: '10 / trang' },
  { value: 20, text: '20 / trang' },
]);

// Mirror BE export constraints — repair-slip.service.js.
const EXPORT_MAX_RANGE_DAYS = 31;
const EXPORT_MAX_LOOKBACK_MONTHS = 3;

// Filter danh sách: mặc định 1 tuần, range tối đa 1 tháng (không giới hạn lookback).
const LIST_DEFAULT_RANGE_DAYS = 7;
const LIST_MAX_RANGE_DAYS = 31;

/**
 * Default filter: từ (hôm nay - 7 ngày) đến hôm nay (DD/MM/YYYY cho date-picker).
 * Tách hàm để dùng lại ở `data()` init và `onResetFilter()`.
 */
const initialFilter = () => ({
  search: '',
  status: '',
  storeIds: [],
  // SRS t101348 — lọc theo vị trí máy hiện tại (currentStoreId).
  currentStoreIds: [],
  fromDate: moment().subtract(LIST_DEFAULT_RANGE_DAYS, 'days').format('DD/MM/YYYY'),
  toDate: moment().format('DD/MM/YYYY'),
  // Ngày trả máy — optional, không default (chỉ lọc khi user chủ động nhập).
  returnedFromDate: '',
  returnedToDate: '',
  // technicianId được set từ selectedTechnician.id (watcher); BE filter qua subquery RepairSlipTechnicians.
  technicianId: null,
  // billNumber dạng số nguyên — BE subquery sang Bills.
  serviceBillNumber: '',
  warrantyBillNumber: '',
});

const TABLE_FIELDS = Object.freeze([
  { key: 'stt', label: 'STT', tdClass: 'text-center', thStyle: { width: '4%' } },
  { key: 'slipNumber', label: 'Số phiếu', thStyle: { width: '12%' } },
  { key: 'customer', label: 'Khách hàng', thStyle: { width: '15%' } },
  { key: 'product', label: 'Thiết bị', thStyle: { width: '18%' } },
  { key: 'warrantyProductName', label: 'Loại BH (SP bảo hành)', thStyle: { width: '14%' } },
  { key: 'currentStore', label: 'Vị trí máy', thStyle: { width: '12%' } },
  { key: 'status', label: 'Trạng thái', tdClass: 'text-center', thStyle: { width: '13%' } },
  { key: 'actions', label: 'Thao tác', tdClass: 'text-center', thStyle: { width: '8%' } },
]);

const API_BASE = 'v2/warranty-service/repair-slips';

export default {
  name: 'ListRepairSlips',
  components: { KTCodePreview, BDropdownCustom, Autosuggest },
  data() {
    return {
      rows: [],
      loading: false,
      filter: initialFilter(),
      page: 1,
      limit: 10,
      totalRow: 0,
      totalPage: 0,
      exporting: false,
      dpConfigs: DP_CONFIG,
      storeOptions: [],
      storeLoading: false,
      // BDropdownCustom v-model = mảng object đã chọn (kèm shortName/storeType).
      selectedStores: [],
      // SRS t101348 — filter vị trí máy hiện tại.
      selectedCurrentStores: [],
      // Permission flags từ /stores/get-by-user — quyết định mặc định pre-select store.
      userDefaultStoreId: null,
      userViewAllStores: false,
      // KTV picker (AutoSuggest): toàn bộ employee fetch 1 lần, filter in-memory theo text.
      // employeeOptionsAll = master list; employeeOptions = list sau filter, feed vào AutoSuggest.
      employeeOptionsAll: [],
      employeeOptions: [],
      technicianSearchText: '',
      selectedTechnician: null,
    };
  },
  computed: {
    // Expose frozen constants — không reactive-wrap mỗi instance.
    statusFilterOptions: () => STATUS_FILTER_OPTIONS,
    pageSizeOptions: () => PAGE_SIZE_OPTIONS,
    fields: () => TABLE_FIELDS,
    exportTooltip: () => `Chỉ xuất được trong ${EXPORT_MAX_LOOKBACK_MONTHS} tháng gần nhất, `
      + `mỗi lần tối đa ${EXPORT_MAX_RANGE_DAYS} ngày (1 tháng).`,
    // Reactive error cho khoảng ngày filter — re-evaluate ngay khi user đổi date-picker.
    // Toast dễ miss (auto-dismiss 5s), inline alert + disable nút Lọc giữ user khỏi click vô ích.
    filterRangeError() {
      return this.validateFilterRange();
    },
    // Expose status value cho template (chỉ delete được khi phiếu còn ở receiving).
    SLIP_STATUS_RECEIVING: () => SLIP_STATUS.RECEIVING,
    canCreate() {
      return checkPermission('WARRANTY_SERVICE_CREATE');
    },
    canDelete() {
      return checkPermission('WARRANTY_SERVICE_DELETE');
    },
    canExport() {
      return checkPermission('WARRANTY_SERVICE_EXPORT');
    },
  },
  async created() {
    // Chờ fetchStores xong để pre-select store (nếu user single-store) trước khi fetch data,
    // tránh case fetch data 2 lần (lần 1 không có storeIds → BE force; lần 2 sau pre-select).
    await this.fetchStores();
    // KTV options preload song song với data — không block (filter không bắt buộc).
    this.fetchEmployees();
    this.fetchData();
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: 'Dịch vụ bảo hành' },
      { title: 'Phiếu sửa chữa' },
    ]);
  },
  watch: {
    // BDropdownCustom bind sang mảng object đã chọn → sync danh sách id (comma-separated)
    // sang `filter.storeIds` để pass query param dạng `Op.in` trên BE.
    selectedStores(newVal) {
      this.filter.storeIds = Array.isArray(newVal)
        ? newVal.map((s) => Number(s.id)).filter((n) => Number.isFinite(n) && n > 0)
        : [];
    },
    // SRS t101348 — filter vị trí máy → filter.currentStoreIds.
    selectedCurrentStores(newVal) {
      this.filter.currentStoreIds = Array.isArray(newVal)
        ? newVal.map((s) => Number(s.id)).filter((n) => Number.isFinite(n) && n > 0)
        : [];
    },
    // AutoSuggest emit object riêng; sync xuống filter.technicianId (BE expect số đơn).
    selectedTechnician(newVal) {
      this.filter.technicianId = newVal && newVal.id ? Number(newVal.id) : null;
    },
  },
  methods: {
    /**
     * Lấy danh sách cửa hàng user được xem — reuse pattern của Bill module.
     * Response shape: { stores: [...], storeId, viewAll, ... }
     *   - viewAll=true (admin/RSM có BILL_VIEW_ALL) → stores = toàn bộ system, KHÔNG pre-select.
     *   - viewAll=false (NV cửa hàng / ASM) → stores = phạm vi được xem, PRE-SELECT store mặc định.
     * BE đã enforce scope theo BILL_VIEW_ALL — FE chỉ giới hạn UX dropdown.
     */
    async fetchStores() {
      this.storeLoading = true;
      try {
        const { data } = await ApiService.query('stores/get-by-user');
        const payload = (data && data.data) || {};
        this.storeOptions = payload.stores || [];
        this.userDefaultStoreId = payload.storeId || null;
        this.userViewAllStores = !!payload.viewAll;
        // Pre-select store mặc định cho NV cửa hàng — họ thường chỉ quan tâm phiếu cửa hàng mình.
        // viewAll = true → không pre-select, để admin chủ động pick.
        if (!this.userViewAllStores && this.userDefaultStoreId) {
          const defaultStore = this.storeOptions.find((s) => s.id === this.userDefaultStoreId);
          if (defaultStore) {
            this.selectedStores = [defaultStore];
            // selectedStores watcher sẽ tự sync filter.storeIds → fetchData sau đó dùng đúng id.
          }
        }
      } catch (err) {
        makeToastFaile('Không tải được danh sách cửa hàng');
      } finally {
        this.storeLoading = false;
      }
    },
    /**
     * Gom params filter (chung cho list query và export).
     * Convert chuỗi/null về undefined để axios drop key — tránh BE nhận empty string làm "0".
     * @returns {Object}
     */
    buildFilterParams() {
      return {
        search: this.filter.search || undefined,
        status: this.filter.status || undefined,
        storeIds: this.filter.storeIds.join(',') || undefined,
        currentStoreIds: this.filter.currentStoreIds.join(',') || undefined,
        fromDate: toIsoDate(this.filter.fromDate) || undefined,
        toDate: toIsoDate(this.filter.toDate) || undefined,
        returnedFromDate: toIsoDate(this.filter.returnedFromDate) || undefined,
        returnedToDate: toIsoDate(this.filter.returnedToDate) || undefined,
        technicianId: this.filter.technicianId || undefined,
        serviceBillNumber: this.filter.serviceBillNumber || undefined,
        warrantyBillNumber: this.filter.warrantyBillNumber || undefined,
      };
    },
    async fetchData() {
      this.loading = true;
      try {
        const { data } = await ApiService.query(API_BASE, {
          params: {
            ...this.buildFilterParams(),
            page: this.page,
            limit: this.limit,
          },
        });
        const payload = data.data || {};
        this.rows = payload.dataset || [];
        this.totalRow = payload.totalRow || 0;
        this.totalPage = payload.totalPage || 0;
      } catch (err) {
        makeToastFaile(extractError(err));
      } finally {
        this.loading = false;
      }
    },
    /**
     * Load toàn bộ employee list 1 lần (xài cho AutoSuggest filter in-memory).
     * Endpoint `/employees/get-list-filter` đã trả gọn (id, name, code) — không cần debounce.
     */
    async fetchEmployees() {
      try {
        const { data } = await ApiService.get('/employees/get-list-filter');
        const employees = (data && data.data) || [];
        this.employeeOptionsAll = employees.map((e) => ({
          id: e.id,
          name: e.name,
          code: e.code,
          suggestionName: e.code ? `${e.name} (${e.code})` : e.name,
          nameNoneSign: xoa_dau(e.name || '').toLowerCase(),
        }));
        this.employeeOptions = this.employeeOptionsAll.slice(0, 20);
      } catch (err) {
        // Filter này optional — fail im lặng để không phá UX page list.
      }
    },
    /**
     * Lọc local theo text typing (dấu / không dấu / mã NV).
     * Empty text → reset selection và show 20 item đầu để user pick nhanh.
     */
    onInputTechnician(text) {
      if (!text || !String(text).trim()) {
        this.selectedTechnician = null;
        this.employeeOptions = this.employeeOptionsAll.slice(0, 20);
        return;
      }
      const kw = xoa_dau(String(text)).toLowerCase();
      this.employeeOptions = this.employeeOptionsAll.filter((e) => {
        return e.nameNoneSign.indexOf(kw) > -1 ||
          (e.code || '').toLowerCase().indexOf(kw) > -1;
      });
    },
    onSelectTechnician(option) {
      if (!option || !option.item) {
        this.selectedTechnician = null;
        return;
      }
      this.selectedTechnician = option.item;
      this.technicianSearchText = option.item.suggestionName;
    },
    /**
     * Validate 1 cặp khoảng ngày — tối đa LIST_MAX_RANGE_DAYS (1 tháng), không giới hạn lookback.
     * @param {string} fromVal - DD/MM/YYYY
     * @param {string} toVal - DD/MM/YYYY
     * @param {string} label - tên hiển thị trong message lỗi
     * @returns {string|null}
     */
    validateOneRange(fromVal, toVal, label) {
      const fromIso = toIsoDate(fromVal);
      const toIso = toIsoDate(toVal);
      if (!fromIso && !toIso) return null;
      if (!fromIso || !toIso) {
        return `Phải nhập cả "Từ" và "Đến" cho ${label}.`;
      }
      const from = moment(fromIso, 'YYYY-MM-DD', true);
      const to = moment(toIso, 'YYYY-MM-DD', true);
      if (!from.isValid() || !to.isValid()) {
        return `Định dạng ngày không hợp lệ ở ${label}.`;
      }
      if (to.isBefore(from, 'day')) {
        return `"${label} đến" phải sau hoặc bằng "${label} từ".`;
      }
      const rangeDays = to.diff(from, 'days');
      if (rangeDays > LIST_MAX_RANGE_DAYS) {
        return `Khoảng ${label} tối đa ${LIST_MAX_RANGE_DAYS} ngày (1 tháng). `
          + `Đang chọn: ${rangeDays} ngày — vui lòng thu hẹp.`;
      }
      return null;
    },
    /**
     * Validate cả Ngày tạo và Ngày trả máy — return lỗi đầu tiên (đủ để UX hiển thị 1 alert).
     * @returns {string|null}
     */
    validateFilterRange() {
      return this.validateOneRange(this.filter.fromDate, this.filter.toDate, 'Ngày tạo')
        || this.validateOneRange(
          this.filter.returnedFromDate, this.filter.returnedToDate, 'Ngày trả máy',
        );
    },
    onFilter() {
      const err = this.validateFilterRange();
      if (err) {
        makeToastFaile(err);
        return;
      }
      this.page = 1;
      this.fetchData();
    },
    onResetFilter() {
      this.filter = initialFilter();
      this.selectedStores = [];
      this.selectedCurrentStores = [];
      this.selectedTechnician = null;
      this.technicianSearchText = '';
      this.employeeOptions = this.employeeOptionsAll.slice(0, 20);
      this.page = 1;
      this.fetchData();
    },
    /**
     * Validate khoảng ngày export (mirror BE _validateExportDateRange).
     * @returns {string|null} message lỗi nếu invalid; null nếu hợp lệ.
     */
    validateExportRange() {
      const fromIso = toIsoDate(this.filter.fromDate);
      const toIso = toIsoDate(this.filter.toDate);
      if (!fromIso || !toIso) {
        return 'Phải chọn cả "Từ ngày" và "Đến ngày" để xuất Excel.';
      }
      const from = moment(fromIso, 'YYYY-MM-DD', true);
      const to = moment(toIso, 'YYYY-MM-DD', true);
      if (!from.isValid() || !to.isValid()) {
        return 'Định dạng ngày không hợp lệ.';
      }
      if (to.isBefore(from, 'day')) {
        return '"Đến ngày" phải sau hoặc bằng "Từ ngày".';
      }
      const earliest = moment().subtract(EXPORT_MAX_LOOKBACK_MONTHS, 'months').startOf('day');
      if (from.isBefore(earliest, 'day')) {
        return `Chỉ được xuất phiếu trong ${EXPORT_MAX_LOOKBACK_MONTHS} tháng gần nhất `
          + `(từ ${earliest.format('DD/MM/YYYY')} trở lại đây).`;
      }
      const rangeDays = to.diff(from, 'days');
      if (rangeDays > EXPORT_MAX_RANGE_DAYS) {
        return `Mỗi lần xuất tối đa ${EXPORT_MAX_RANGE_DAYS} ngày (1 tháng). `
          + `Khoảng đang chọn: ${rangeDays} ngày — vui lòng thu hẹp.`;
      }
      return null;
    },
    async onExportExcel() {
      const validationError = this.validateExportRange();
      if (validationError) {
        makeToastFaile(validationError);
        return;
      }
      this.exporting = true;
      try {
        const response = await ApiService.query('warranty-service-export/repair-slips', {
          params: this.buildFilterParams(),
          responseType: 'blob',
        });
        let filename = `phieu-sua-chua-${moment().format('YYYYMMDD-HHmmss')}.xlsx`;
        try {
          filename = fileDownload.getFileName(response);
        } catch (_e) {
          // BE không trả Content-Disposition đúng format → fallback tên trên
        }
        fileDownload.createFile(
          response.data,
          filename,
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        );
        makeToastSuccess('Đã xuất file Excel');
      } catch (err) {
        // Lỗi BE trả blob (vì responseType=blob) — phải đọc text trước
        let msg = 'Không xuất được file';
        if (err && err.response && err.response.data) {
          try {
            const text = await err.response.data.text();
            const json = JSON.parse(text);
            msg = json.message || msg;
          } catch (_parseErr) {
            // ignore
          }
        }
        makeToastFaile(msg);
      } finally {
        this.exporting = false;
      }
    },
    // SRS t101348 — máy có đang ở cửa hàng gốc không (phiếu cũ chưa có currentStoreId → coi như gốc).
    isAtOriginStore(item) {
      if (item.currentStoreId == null) return true;
      return Number(item.currentStoreId) === Number(item.storeId);
    },
    isTransferring(item) {
      return Number(item.transferStatus) === TRANSFER_STATUS.SHIPPING;
    },
    onPageChange(newPage) {
      this.page = newPage;
      this.$nextTick(() => this.fetchData());
    },
    // Đổi page size → reset về trang 1 vì page hiện tại có thể vượt totalPage mới.
    onChangePageSize() {
      this.page = 1;
      this.fetchData();
    },

    statusLabel(value) {
      const opt = STATUS_OPTIONS.find((o) => o.value === value);
      return opt ? opt.text : value;
    },
    statusVariant(value) {
      const opt = STATUS_OPTIONS.find((o) => o.value === value);
      return opt ? opt.variant : 'secondary';
    },
    formatDateTime(value) {
      return formatDateTime(value);
    },

    goToCreate() {
      this.$router.push({ name: 'create-repair-slip' });
    },
    goToEdit(id) {
      this.$router.push({ name: 'edit-repair-slip', params: { id } });
    },
    /**
     * SRS t101348 — phiếu có cần lập HĐ bán lẻ linh kiện không.
     * `hasBillableParts` (BE) đã bao hàm: status completed/notified + chưa có HĐ bán lẻ +
     * còn linh kiện giá > 0. FE chỉ thêm điều kiện máy đã về CH gốc + quyền cập nhật.
     * @param {Object} item — row của bảng
     * @returns {boolean}
     */
    canCreatePartsBill(item) {
      return !!item.hasBillableParts
        && this.isAtOriginStore(item)
        && checkPermission('WARRANTY_SERVICE_UPDATE');
    },
    goToCreatePartsBill(id) {
      this.$router.push({ name: 'add-bill', query: { fromRepairSlipId: id } });
    },
    /**
     * Mở Bill liên kết (bán lẻ / bảo hành) ở tab mới — pattern giống RepairSlipForm.openLinkedBill.
     * @param {Object} item — row của bảng (cần item.serviceBill / item.warrantyBill có billNumber)
     * @param {'service'|'warranty'} kind
     */
    openLinkedBill(item, kind) {
      const billNumber = kind === 'service'
        ? item.serviceBill && item.serviceBill.billNumber
        : item.warrantyBill && item.warrantyBill.billNumber;
      if (!billNumber) return;
      const route = kind === 'service'
        ? { name: 'update-bill', query: { id: billNumber } }
        : { name: 'add-warranty-bill-v2', query: { billId: billNumber, action: 'update' } };
      const resolved = this.$router.resolve(route);
      window.open(resolved.href, '_blank');
    },
    async onDelete(item) {
      const ok = await this.$bvModal.msgBoxConfirm(
        `Xoá phiếu "${item.slipNumber}" của ${item.customerName}?`,
        {
          title: 'Xác nhận xoá',
          okTitle: 'Xoá',
          cancelTitle: 'Huỷ',
          okVariant: 'danger',
          centered: true,
        },
      );
      if (!ok) return;
      try {
        await ApiService.delete(`${API_BASE}/${item.id}`);
        makeToastSuccess('Đã xoá phiếu sửa chữa');
        this.fetchData();
      } catch (err) {
        makeToastFaile(extractError(err));
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.list-repair-slips {
  ::v-deep .table-vertical-center td {
    vertical-align: middle;
  }

  .btn-filter-apply {
    flex: 1;
  }

  // Filter row — mỗi col 1 control, đồng đều chiều cao + align center dọc.
  // Tham khảo style của trang Danh sách đơn hàng (md=2 đều nhau / hàng).
  ::v-deep .filter-row {
    align-items: center;

    // Mọi loại control ép cùng chiều cao ~31px để vertical align giữa các col khác nhau.
    .form-control-sm,
    .form-control,
    .custom-select-sm,
    .autosuggest__input {
      height: 31px;
      font-size: 0.875rem;
    }

    .dropdown-toggle {
      min-height: 31px;
      padding-top: 4px;
      padding-bottom: 4px;
    }

    // Autosuggest wrapper có margin mặc định — bỏ để align thẳng với input chuẩn.
    .auto-autosuggest-wrap-component {
      width: 100%;
    }
  }

  .list-total-label {
    font-weight: 500;
  }

  ::v-deep .action-item-default {
    color: #3f4254;
    font-size: 12px;
  }

  ::v-deep .action-item-danger {
    color: #f64e60;
    font-size: 12px;
  }

  // HĐ liên kết trong dropdown actions — phân biệt rõ với "Xem / Sửa" và "Xoá".
  ::v-deep .action-item-link {
    color: #3699ff;
    font-size: 12px;
  }
  ::v-deep .action-item-warranty {
    color: #ff9800;
    font-size: 12px;
  }
  // SRS t101348 — "Lập HĐ bán lẻ linh kiện" (xanh lá, phân biệt với link HĐ đã có).
  ::v-deep .action-item-bill {
    color: #1bc5bd;
    font-size: 12px;
  }

  // SRS t101348 — chip "Đang luân chuyển": pill mềm, viền nhạt, chấm pulse
  // thay cho badge cam đặc cũ — hài hoà hơn khi đứng dưới badge trạng thái.
  .transfer-chip {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 3px 10px 3px 8px;
    font-size: 11px;
    font-weight: 600;
    line-height: 1;
    color: #b06a00;
    background: #fff5e6;
    border: 1px solid #ffd591;
    border-radius: 999px;
    white-space: nowrap;

    &__icon {
      font-size: 11px;
      color: #fa8c16;
    }

    &__dot {
      width: 7px;
      height: 7px;
      border-radius: 50%;
      background: #fa8c16;
      box-shadow: 0 0 0 0 rgba(250, 140, 22, 0.5);
      animation: transfer-pulse 1.6s ease-out infinite;
    }
  }

  @keyframes transfer-pulse {
    0% {
      box-shadow: 0 0 0 0 rgba(250, 140, 22, 0.5);
    }
    70% {
      box-shadow: 0 0 0 6px rgba(250, 140, 22, 0);
    }
    100% {
      box-shadow: 0 0 0 0 rgba(250, 140, 22, 0);
    }
  }
}
</style>
